import axios from "axios";
import { API_BASE_URL, API_PATHS } from "../config/api";
import { isJwtExpired } from "../utils/jwt";

export const AUTH_ACCESS_STORAGE_KEY = "haemil_access_token";
export const AUTH_USER_STORAGE_KEY = "haemil_auth_user";
const LEGACY_REFRESH_KEY = "haemil_refresh_token";
const authSessionExpiredListeners = new Set();

let authSessionInvalidated = false;
let accessToken = null;

// 이전 버전이 남긴 브라우저 저장 토큰은 다시 사용하지 않는다.
if (typeof window !== "undefined") {
  window.localStorage.removeItem(AUTH_ACCESS_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_REFRESH_KEY);
}

export function getStoredTokens() {
  return {
    access: accessToken,
  };
}

export function storeTokens({ access }) {
  if (access) {
    accessToken = access;
    authSessionInvalidated = false;
  }
  localStorage.removeItem(AUTH_ACCESS_STORAGE_KEY);
  localStorage.removeItem(LEGACY_REFRESH_KEY);
}

export function getStoredAuthUser() {
  try {
    const stored = JSON.parse(localStorage.getItem(AUTH_USER_STORAGE_KEY));
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    return stored;
  } catch {
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    return null;
  }
}

export function storeAuthUser(user) {
  if (!user || typeof user !== "object") {
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    return;
  }

  const cachedUser = {
    id: user.id ?? null,
    username: user.username || "",
    role: user.role || "",
    can_complete_cs: user.can_complete_cs === true,
    can_complete_cs_effective: user.can_complete_cs_effective === true,
  };
  localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(cachedUser));
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem(AUTH_ACCESS_STORAGE_KEY);
  localStorage.removeItem(AUTH_USER_STORAGE_KEY);
  localStorage.removeItem(LEGACY_REFRESH_KEY);
}

export function subscribeToAuthSessionExpired(listener) {
  authSessionExpiredListeners.add(listener);
  return () => authSessionExpiredListeners.delete(listener);
}

export function invalidateAuthSession(reason = "session_expired") {
  clearTokens();
  if (authSessionInvalidated) return;

  authSessionInvalidated = true;
  authSessionExpiredListeners.forEach((listener) => {
    listener({ reason });
  });
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
});

let refreshPromise = null;

function isRefreshCredentialFailure(error) {
  const status = error?.response?.status;
  return status === 400 || status === 401;
}

function isAuthBypassPath(url) {
  const reqUrl = String(url || "").split("?")[0];
  return (
    reqUrl.endsWith(API_PATHS.login) ||
    reqUrl.endsWith(API_PATHS.logout) ||
    reqUrl.endsWith(API_PATHS.refresh)
  );
}

function createSessionExpiredError(config) {
  const error = new Error("Authentication session has expired.");
  error.code = "AUTH_SESSION_EXPIRED";
  error.config = config;
  return error;
}

// refresh는 interceptor 재귀 방지 위해 axios로 직접 호출한다.
async function refreshAccessToken() {
  const res = await axios.post(
    `${API_BASE_URL}${API_PATHS.refresh}`,
    {},
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    }
  );
  return res.data?.access;
}

async function getRefreshedAccess() {
  if (!refreshPromise) {
    const accessAtRefreshStart = getStoredTokens().access;

    refreshPromise = refreshAccessToken()
      .then((newAccess) => {
        const currentAccess = getStoredTokens().access;
        if (currentAccess !== accessAtRefreshStart) {
          return currentAccess || null;
        }
        if (newAccess) storeTokens({ access: newAccess });
        return newAccess || null;
      })
      .catch((error) => {
        const currentAccess = getStoredTokens().access;
        if (currentAccess && currentAccess !== accessAtRefreshStart) return currentAccess;
        if (isRefreshCredentialFailure(error)) return null;
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.request.use(async (config) => {
  // ✅ headers가 undefined일 수 있으니 항상 보장
  config.headers = config.headers || {};
  if (isAuthBypassPath(config.url)) return config;

  const { access } = getStoredTokens();
  if (!access) return config;

  if (isJwtExpired(access)) {
    const newAccess = await getRefreshedAccess();
    if (newAccess) {
      config.headers.Authorization = `Bearer ${newAccess}`;
    } else {
      invalidateAuthSession("refresh_failed");
      throw createSessionExpiredError(config);
    }
    return config;
  }

  config.headers.Authorization = `Bearer ${access}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error?.config;
    const status = error?.response?.status;

    if (status !== 401 || !originalConfig || isAuthBypassPath(originalConfig.url)) {
      return Promise.reject(error);
    }

    if (originalConfig._retry) {
      invalidateAuthSession("unauthorized_after_refresh");
      return Promise.reject(error);
    }

    originalConfig._retry = true;
    const newAccess = await getRefreshedAccess();
    if (!newAccess) {
      invalidateAuthSession("refresh_failed");
      return Promise.reject(error);
    }

    originalConfig.headers = originalConfig.headers || {};
    originalConfig.headers.Authorization = `Bearer ${newAccess}`;
    return api(originalConfig);
  }
);
